"use client";

import React, { useEffect, useRef } from "react";
import { ZodSchema, z } from "zod";

import { WizardStepNavigationButtons } from "./WizardStepNavigationButtons";

import { FormHandle } from "../Form/types";
import { WizardStepProps, WizardStepRenderProps } from "./types";

import { PYForm } from "../Form";
import { useWizard } from "./context";

export const WizardStep = <TSchema extends ZodSchema = ZodSchema>({
	id,
	title,
	schema,
	defaultValues,
	canSkip = false,
	beforeNext,
	children,
}: WizardStepProps<TSchema>) => {
	const {
		wizardData,
		currentStep,
		totalSteps,
		isFirstStep,
		isLastStep,
		goToStep,
		goToNext,
		goToPrevious,
		setStepData,
		registerStep,
		setStepFormRef,
		wizardId,
		completedSteps,
		performNavigation,
	} = useWizard();

	const formRef = useRef<FormHandle<TSchema> | null>(null);

	useEffect(() => {
		registerStep({
			id,
			title,
			schema,
			defaultValues: defaultValues as Record<string, unknown> | undefined,
			canSkip,
			beforeNext,
		});
	}, [id, title, schema, defaultValues, canSkip, beforeNext, registerStep]);

	useEffect(() => {
		setStepFormRef(id, formRef.current);
		return () => {
			setStepFormRef(id, null);
		};
	}, [id, setStepFormRef]);

	const renderProps: WizardStepRenderProps<TSchema> = {
		form: formRef.current,
		wizardData,
		goToStep,
		goToNext,
		goToPrevious,
		currentStep,
		totalSteps,
		isFirstStep,
		isLastStep,
		completedSteps,
	};

	const handleFormSubmit = async ({ value }: { value: unknown }) => {
		setStepData(id, value);
		await performNavigation({ stepId: id, data: value });
	};

	const stepData = ((wizardData[id] as typeof defaultValues) || defaultValues || {}) as z.output<TSchema>;

	if (schema) {
		return (
			<div data-wizard-step-id={id} className="h-full overflow-auto">
				<PYForm
					ref={formRef}
					id={`${wizardId}-${id}`}
					schema={schema}
					defaultValues={stepData}
					onSubmit={handleFormSubmit}
					dangerouslySetClassName="flex min-h-full flex-col"
				>
					<div className="flex-1">{typeof children === "function" ? children(renderProps) : children}</div>
					<WizardStepNavigationButtons />
				</PYForm>
			</div>
		);
	}

	return (
		<div data-wizard-step-id={id} className="h-full overflow-auto">
			<div className="flex min-h-full flex-col">
				<div className="flex-1">{typeof children === "function" ? children(renderProps) : children}</div>
				<WizardStepNavigationButtons />
			</div>
		</div>
	);
};

WizardStep.displayName = "WizardStep";
